import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import dayjs from 'dayjs'
import specializedReportService from '@api/v1/specializedReportService'
import reportJobService from '@api/v1/reportJobService'
import useLatestRun from '@hooks/useLatestRun'
import SpecializedReportShell from '@components/reports/SpecializedReportShell'
import SpecializedFilterBar from '@components/reports/SpecializedFilterBar'
import { dateRangeField } from '@components/reports/specializedFilterFields'
import ReportViewsMenu from '@components/reports/ReportViewsMenu'
import useSavedReportView from '@components/reports/useSavedReportView'
import { describeFilters, dateRangePart } from '@components/reports/reportFilterSummary'
import AutoReportGrid from '@components/reports/AutoReportGrid'
import { PAYMENTS_BY_CASE_COLUMNS, flattenPaymentsByCase } from './paymentsByCaseRows'

// Spec 203: exactly legacy's one filter and two keys (PaymentsByCaseHomeView.js:116-117).
// CB's proc reads only these two dates (AccountingHelper.Reports.cs:2345-2346). This page
// used to send `invoiceDateStart`/`invoiceDateEnd`, which bind and are then ignored, so
// the proc saw no dates and matched nothing; and it offered Client and Originator
// filters the proc never reads, which would have scoped nothing while appearing to.
// `omitAll` keeps both dates present: without them cb-api refuses the run.
const FIELDS = [
  dateRangeField({
    key: 'paymentAllocated',
    label: 'Payment Allocated Date',
    startKey: 'paymentReceivedDateStart',
    endKey: 'paymentReceivedDateEnd',
    omitAll: true,
  }),
]

const makeDefaults = () => ({
  paymentReceivedDateStart: dayjs().startOf('year').format('YYYY-MM-DD'),
  paymentReceivedDateEnd: dayjs().format('YYYY-MM-DD'),
})

// The wire object: exactly the two keys legacy sends, so a saved view or a stored
// schedule carrying the old keys cannot reach CB with them.
const toWire = (f) => ({
  paymentReceivedDateStart: f.paymentReceivedDateStart || null,
  paymentReceivedDateEnd: f.paymentReceivedDateEnd || null,
})

// Module-level so useLatestRun's `run` keeps one identity (spec 131).
const runErrorMessage = (err) => err.message || 'Report failed'

const PaymentsByCaseReport = ({ reportId, name = 'Payments by Case', summary, jobId }) => {
  const [filters, setFilters] = useState(makeDefaults)
  // Spec 131: only the NEWEST run may write the rows, the spinner, the error or
  // the documents' criteria (the split button's mid-run "Run report now" is also
  // disabled now; this gate is the structural guarantee).
  const {
    running, error, ran, runKey, run: startRun,
  } = useLatestRun({ initialRunning: !!jobId, errorMessage: runErrorMessage })
  // CC-50013 (G3): saved views. The hook carries the grid's page state, so
  // the menu cannot be mounted without it (the CC-49732 defect).
  const view = useSavedReportView(setFilters)
  // CC-50029: exports state the period they cover.
  const filtersSummary = useMemo(() => describeFilters(dateRangePart(filters.paymentReceivedDateStart, filters.paymentReceivedDateEnd)), [filters])
  const [sections, setSections] = useState([])
  const rows = useMemo(() => flattenPaymentsByCase(sections), [sections])
  const [savedAsOf, setSavedAsOf] = useState(null)
  // Consume-once: the first run reopens the tray's saved job; later runs are fresh.
  const jobRef = useRef(jobId || null)

  const filterPayload = useMemo(() => toWire(filters), [filters])

  const runReport = useCallback(() => startRun(async () => {
    const jid = jobRef.current
    jobRef.current = null // consumed — subsequent runs are fresh
    if (jid) {
      // Tray "View report" → reopen the exact saved snapshot + show its run time.
      const [st, r] = await Promise.all([reportJobService.status(jid), reportJobService.result(jid)])
      return () => { setSavedAsOf(st?.createdAt || null); setSections(r?.data?.rows || []) }
    }
    const result = await specializedReportService.paymentsByCase.run(filterPayload, { reportId })
    // Spec 131 decision 3: the saved-result banner clears at the COMMIT.
    return () => { setSavedAsOf(null); setSections(result || []) }
  }, { criteria: { filtersSummary } }), [filterPayload, reportId, startRun, filtersSummary])

  // Spec 131: deferred and cancellable, so StrictMode's simulated remount starts
  // ONE mount run, not two.
  useEffect(() => {
    const t = setTimeout(() => { runReport() }, 0)
    return () => clearTimeout(t)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // CC-50013 G2 / CC-50148: shared metadata-driven bar (see FIELDS).
  const filterBar = (
    <SpecializedFilterBar
      fields={FIELDS}
      filters={filters}
      onFiltersChange={setFilters}
      defaults={makeDefaults()}
      onRun={runReport}
      isLoading={running}
      queue={{
        reportId,
        name,
        actionKey: 'payments-by-case',
        endpoint: (f) => ({ method: 'POST', path: '/reports/payments-by-case', body: toWire(f) }),
      }}
      actions={(
        <ReportViewsMenu
          storageKey="report:payments-by-case"
          currentFilters={filters}
          onApply={view.applyView}
          getPageState={view.getPageState}
        />
      )}
    />
  )

  // No `download`: CB has no file for this report (its view model is not an
  // ILegacyReportAdapter, AccountingModels.cs:333), so the old server Export could
  // only fail. Without one the shell exports the grid's own rows.
  return (
    <SpecializedReportShell
      name={name} summary={summary} running={running} error={error}
      ranCriteria={ran?.criteria ?? null} runKey={runKey}
      filterBar={filterBar} filtersSummary={filtersSummary} savedAsOf={savedAsOf}
    >
      <AutoReportGrid key={view.gridKey} rows={rows} columns={PAYMENTS_BY_CASE_COLUMNS} storageKey="report:payments-by-case" pageStateRef={view.pageStateRef} initialPageState={view.initialPageState} />
    </SpecializedReportShell>
  )
}

export default PaymentsByCaseReport
